import { appPath } from '@/lib/appUrl';
import { store } from '@/storage/sqliteStore';
import { supabase, supabaseConfigured } from '@/lib/supabaseClient';
import { ensureCloudProfile, getCloudUser, pushRecord, syncFromCloud, syncNow } from '@/lib/cloudSync';
import { isAtLeast } from '@/lib/roles';
import { ensureTenant, getActiveTenantId, clearTenantContext, requireActiveLicence } from '@/lib/tenantContext';

import { safeLocalStorage } from '@/lib/browserStorage';
const now = () => new Date().toISOString();
const id = () => `${Date.now()}-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;

async function currentUser() {
  if (safeLocalStorage.getItem('drilllog_explicitly_logged_out') === '1') return null;
  const userId = safeLocalStorage.getItem('drilllog_user_id');
  if (supabaseConfigured && supabase) {
    const cloudUser = await getCloudUser();
    const resolvedId = cloudUser?.id || userId;
    if (resolvedId) {
      const local = await store('User').get(resolvedId);
      if (local) return local;
      // Only create/update the cloud profile when online. An existing
      // persisted Supabase session is sufficient to use the app offline.
      if (cloudUser) {
        const profile = await ensureCloudProfile(cloudUser);
        const user = { ...profile, auth_user_id: cloudUser.id };
        await store('User').put(user);
        return user;
      }
    }
  }
  if (!userId) return null;
  return store('User').get(userId);
}

async function withMeta(data) {
  const user = await currentUser();
  const organisationId = await getActiveTenantId().catch(() => null);
  return {
    ...data,
    id: data.id || id(),
    created_date: data.created_date || now(),
    updated_date: now(),
    created_by_id: data.created_by_id || user?.id || null,
    organisation_id: data.organisation_id || organisationId || null,
  };
}

function sortItems(items, sort) {
  if (!sort) return items;
  const descending = String(sort).startsWith('-');
  const field = String(sort).replace(/^-/, '');
  return [...items].sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    if (av === bv) return 0;
    if (av == null) return descending ? 1 : -1;
    if (bv == null) return descending ? -1 : 1;
    const result = av > bv ? 1 : -1;
    return descending ? -result : result;
  });
}

function matches(item, filter = {}) {
  return Object.entries(filter).every(([key, value]) => {
    if (Array.isArray(value)) return value.includes(item[key]);
    return item[key] === value;
  });
}

async function writeAudit(action, entityName, recordId, details = {}) {
  if (entityName === "AuditLog") return;
  try {
    const actor = await currentUser();
    if (!actor) return;
    const audit = {
      id: id(),
      user_id: actor.id,
      user_name: actor.full_name || actor.name || actor.email || "Unknown",
      action,
      entity: entityName,
      record_id: recordId || "",
      device: safeLocalStorage.getItem("drilllog_device_name") || "Unknown device",
      details,
      occurred_at: now(),
      created_date: now(),
      updated_date: now(),
    };
    await store("AuditLog").put(audit);
    void pushRecord(actor.id, "AuditLog", audit);
  } catch (error) {
    console.warn("[DrillLogPro] audit write skipped:", error?.message || error);
  }
}

function entity(name) {
  const table = store(name);
  return {
    async list(sort, limit) {
      const items = sortItems(await table.all(), sort);
      return limit ? items.slice(0, limit) : items;
    },
    async filter(filter = {}, sort, limit) {
      const items = (await table.all()).filter((item) => matches(item, filter));
      const sorted = sortItems(items, sort);
      return limit ? sorted.slice(0, limit) : sorted;
    },
    async get(recordId) {
      const value = await table.get(recordId);
      if (!value) throw new Error(`${name} not found`);
      return value;
    },
    async create(data) {
      const record = await withMeta(data);
      await table.add(record);
      const user = await currentUser();
      void pushRecord(user?.id, name, record);
      void writeAudit("created", name, record.id);
      return record;
    },
    async bulkCreate(records = []) {
      const prepared = [];
      for (const record of records) prepared.push(await withMeta(record));
      await table.bulkPut(prepared);
      const user = await currentUser();
      prepared.forEach((record) => void pushRecord(user?.id, name, record));
      prepared.forEach((record) => void writeAudit("created", name, record.id));
      return prepared;
    },
    async update(recordId, updates) {
      const existing = await table.get(recordId);
      if (!existing) throw new Error(`${name} not found`);
      const updated = { ...existing, ...updates, updated_date: now() };
      await table.put(updated);
      const user = await currentUser();
      void pushRecord(user?.id, name, updated);
      void writeAudit("updated", name, recordId);
      return updated;
    },
    async bulkUpdate(updates = []) {
      const updated = [];
      for (const entry of updates) {
        const record = await table.get(entry.id);
        if (!record) continue;
        const value = { ...record, ...entry, updated_date: now() };
        await table.put(value);
        updated.push(value);
      }
      const user = await currentUser();
      updated.forEach((record) => void pushRecord(user?.id, name, record));
      updated.forEach((record) => void writeAudit("updated", name, record.id));
      return updated;
    },
    async delete(recordId) {
      const existing = await table.get(recordId);
      await table.delete(recordId);
      const user = await currentUser();
      if (existing) {
        void pushRecord(user?.id, name, existing, true);
        void writeAudit("deleted", name, recordId);
      }
    },
    async deleteMany(filter = {}) {
      const records = (await table.all()).filter((item) => matches(item, filter));
      await table.bulkDelete(records.map((item) => item.id));
      const user = await currentUser();
      records.forEach((record) => {
        void pushRecord(user?.id, name, record, true);
        void writeAudit("deleted", name, record.id);
      });
    },
    async bulkDelete(ids = []) {
      for (const recordId of ids) await this.delete(recordId);
    },
  };
}

const auth = {
  async me() {
    if (supabaseConfigured && supabase) {
      const cloudUser = await getCloudUser();
      const storedUserId = safeLocalStorage.getItem('drilllog_user_id');
      const resolvedId = cloudUser?.id || storedUserId;
      if (!resolvedId) throw Object.assign(new Error('Authentication required'), { status: 401 });

      let local = await store('User').get(resolvedId);
      if (!local && cloudUser) {
        const profile = await ensureCloudProfile(cloudUser);
        local = { ...profile, auth_user_id: cloudUser.id };
        await store('User').put(local);
      }
      if (!local) throw Object.assign(new Error('Authentication required'), { status: 401 });

      safeLocalStorage.setItem('drilllog_user_id', resolvedId);
      safeLocalStorage.removeItem('drilllog_explicitly_logged_out');
      // Cloud sync is best-effort. Never make an offline user look logged out
      // just because Supabase cannot be reached.
      if (cloudUser) {
        try { await syncFromCloud(cloudUser.id, store); } catch (syncError) { console.warn('[DrillLogPro] cloud pull failed:', syncError.message); }
      }
      return local;
    }
    const user = await currentUser();
    if (!user) throw Object.assign(new Error('Cloud authentication is not configured'), { status: 503 });
    return user;
  },

  async register({ email, password, name = '' }) {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY to .env.');
    const normalized = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signUp({
      email: normalized,
      password,
      options: {
        data: { name: name.trim() || normalized.split('@')[0], role: 'rig' },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data.user && data.session) {
      await ensureTenant({ organisationName: name.trim() ? `${name.trim()}'s Organisation` : undefined });
      const profile = await ensureCloudProfile(data.user, { name: name.trim() || normalized.split('@')[0], role: 'rig' });
      await store('User').put({ ...profile, auth_user_id: data.user.id });
      safeLocalStorage.setItem('drilllog_user_id', data.user.id);
      safeLocalStorage.setItem('drilllog_offline_auth_user', JSON.stringify({
        id: data.user.id,
        auth_user_id: data.user.id,
        email: data.user.email || normalized,
        name: name.trim() || normalized.split('@')[0],
        role: 'rig',
      }));
    }
    return data;
  },

  async loginViaEmailPassword(email, password) {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const normalized = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signInWithPassword({ email: normalized, password });
    if (error) {
      if (error.code === 'email_not_confirmed' || /email not confirmed/i.test(error.message || '')) {
        const resendError = await supabase.auth.resend({ type: 'signup', email: normalized });
        if (resendError.error) {
          const wrapped = new Error(`${error.message}. A new verification email could not be sent automatically: ${resendError.error.message}`);
          wrapped.code = error.code;
          throw wrapped;
        }
        const wrapped = new Error('Your email is not verified. We sent a new verification email. Check your inbox and click the newest link.');
        wrapped.code = 'email_not_confirmed';
        throw wrapped;
      }
      throw error;
    }
    const user = data.user;
    const existing = await store('User').get(user.id);
    await ensureTenant();
    await requireActiveLicence();
    const profile = await ensureCloudProfile(user, existing || {});
    await store('User').put({ ...profile, auth_user_id: user.id });
    safeLocalStorage.setItem('drilllog_user_id', user.id);
    safeLocalStorage.setItem('drilllog_offline_auth_user', JSON.stringify({
      id: user.id,
      auth_user_id: user.id,
      email: user.email || normalized,
      name: profile.name || normalized.split('@')[0],
      role: profile.role || 'rig',
    }));
    safeLocalStorage.removeItem('drilllog_explicitly_logged_out');
    try { await syncFromCloud(user.id, store); } catch (syncError) { console.warn('[DrillLogPro] cloud pull skipped:', syncError.message); }
    return profile;
  },

  async verifyOtp() { throw new Error('Email verification is handled by Supabase. Check your email and then log in.'); },
  async resendOtp(email) {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { error } = await supabase.auth.resend({ type: 'signup', email: email.trim().toLowerCase() });
    if (error) throw error;
    return { ok: true };
  },
  async resetPasswordRequest(email) {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
    return { ok: true };
  },
  async resetPassword({ newPassword }) {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { ok: true };
  },
  setToken() {},
  async logout() {
    safeLocalStorage.setItem('drilllog_explicitly_logged_out', '1');
    if (supabaseConfigured && supabase) {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) console.warn('[DrillLogPro] Supabase sign-out warning:', error.message);
    }
    safeLocalStorage.removeItem('drilllog_user_id');
    safeLocalStorage.removeItem('drilllog_offline_auth_user');
    clearTenantContext();
  },
  redirectToLogin() { window.location.href = appPath('/login'); },
  loginWithProvider(provider, redirectPath = '/') {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    return supabase.auth.signInWithOAuth({ provider, options: { redirectTo: `${window.location.origin}${redirectPath}` } });
  },
};

async function uploadFile({ file }) {
  if (!file) throw new Error('No file supplied');
  const fileId = id();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  await store('File').put({ id: fileId, name: file.name, type: file.type, data: dataUrl, created_date: now(), updated_date: now() });
  return { file_url: dataUrl, id: fileId };
}

async function invokeFunction(name, payload = {}) {
  if (name === 'acceptInvite') {
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) throw new Error('No authenticated invitation session is available.');
    const { data, error } = await supabase.functions.invoke('accept-invite', { body: {}, headers: { Authorization: `Bearer ${accessToken}` } });
    if (error) throw error;
    return data || { ok: true };
  }
  if (name === 'adminUserManagement') {
    const admin = await currentUser();
    if (!isAtLeast(admin?.role, 'admin')) throw new Error('Admin access required.');
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    const requestBody = { ...payload };
    const { data, error } = await supabase.functions.invoke('admin-user-management', { body: requestBody, headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined });
    if (error) {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-user-management`;
      const response = await fetch(functionUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) }, body: JSON.stringify(requestBody) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || body?.message || `User administration failed (${response.status})`);
      return body || { ok: true };
    }
    return data || { ok: true };
  }
  if (name === 'adminDeviceSessions') {
    const admin = await currentUser();
    if (!isAtLeast(admin?.role, 'admin')) throw new Error('Admin access required.');
    if (!payload.user_id) throw new Error('User is required.');
    if (!supabaseConfigured || !supabase) throw new Error('Cloud authentication is not configured.');
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    const requestBody = {
      action: payload.action,
      user_id: payload.user_id,
      auth_user_id: payload.auth_user_id || null,
      email: payload.email || null,
      device_id: payload.device_id || null,
    };
    const { data, error } = await supabase.functions.invoke('admin-device-sessions', {
      body: requestBody,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    });
    if (error) {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-device-sessions`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || body?.message || `Device request failed (${response.status})`);
      return body || { ok: true };
    }
    return data || { ok: true };
  }
  if (name === 'requestAccountRecovery') {
    const requester = await currentUser();
    const email = String(payload.email || requester?.email || '').trim().toLowerCase();
    if (!email) throw new Error('Email is required.');
    if (supabaseConfigured && supabase) {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const requestBody = { email, reason: payload.reason || 'Password recovery requested' };
      const { data, error } = await supabase.functions.invoke('account-recovery-request', {
        body: requestBody,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (!error) return data || { ok: true };
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/account-recovery-request`;
      try {
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(requestBody),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.error || body?.message || `Recovery request failed (${response.status})`);
        return body || { ok: true };
      } catch (fallbackError) {
        throw new Error(fallbackError?.message || error.message || 'Failed to send a request to the Edge Function.');
      }
    }
    const request = await entity('AccountRecoveryRequest').create({
      requester_user_id: requester?.id || null,
      requester_email: email,
      requested_at: now(),
      status: 'pending',
      reason: payload.reason || 'Password recovery requested',
    });
    return { request };
  }
  if (name === 'requestAccountDeletion') {
    const user = await currentUser();
    if (!user) throw new Error('Authentication required');
    const request = await entity('AccountDeletionRequest').create({
      user_id: user.id, user_email: user.email, user_name: user.name || user.email,
      status: 'pending', requested_at: now(),
      expires_at: new Date(Date.now() + 86400000).toISOString(),
    });
    return { request };
  }
  throw new Error(`Unsupported cloud function: ${name}`);
}

export const standaloneDB = { table: store };
export const base44 = {
  entities: {
    Sheet: entity('Sheet'), DrillRun: entity('DrillRun'), DepthAlert: entity('DepthAlert'),
    Stocktake: entity('Stocktake'), User: entity('User'), DailyLog: entity('DailyLog'), DailyTask: entity('DailyTask'),
    AccountDeletionRequest: entity('AccountDeletionRequest'),
    AccountRecoveryRequest: entity('AccountRecoveryRequest'),
    AuditLog: entity('AuditLog'),
    ShiftSnapshot: entity('ShiftSnapshot'),
    DocumentTemplate: entity('DocumentTemplate'),
    AirCoreRun: entity('AirCoreRun'),
    AirCoreSample: entity('AirCoreSample'),
    RCRun: entity('RCRun'),
    FeaturePermission: entity('FeaturePermission'),
  },
  users: {},
  auth,
  functions: { invoke: invokeFunction },
  integrations: { Core: { UploadFile: uploadFile, async SendEmail() { return { ok: true, local: true }; } } },
};
